import { Auth } from "aws-amplify";
import { STSClient, AssumeRoleCommand } from "@aws-sdk/client-sts";
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, QueryCommand } from "@aws-sdk/lib-dynamodb";
import { isProd } from "src/util/is-prod";

interface InvoiceTrackerConfig {
  roleArn: string;
  tableName: string;
  region: string;
}

const BETA_CONFIG: InvoiceTrackerConfig = {
  roleArn: "arn:aws:iam::730335579107:role/FuseInvoiceTracker-PortalRead-beta",
  tableName: "FuseInvoiceTracker-beta",
  region: "us-east-1",
};

const PROD_CONFIG: InvoiceTrackerConfig = {
  roleArn: "arn:aws:iam::637423481244:role/FuseInvoiceTracker-PortalRead-prod",
  tableName: "FuseInvoiceTracker-prod",
  region: "us-east-1",
};

function getConfig(): InvoiceTrackerConfig {
  return isProd() ? PROD_CONFIG : BETA_CONFIG;
}

/**
 * Maps FACD serviceProvider to Centauri product_code for DDB PK construction.
 * Source: confluencear_dwh.ar_invoice_all.product_code column.
 */
const SERVICE_PROVIDER_TO_PRODUCT_CODE: Record<string, string> = {
  PRIME: "1100",
  VIDEO: "1415",
  PVGO: "1415",
};

/** Returns the DDB partition key: resellerId#productCode#channelCode, or null if serviceProvider is unknown. */
export function buildTrackerPK(
  resellerId: string,
  serviceProvider: string,
  channelCode?: string,
): string | null {
  const productCode = SERVICE_PROVIDER_TO_PRODUCT_CODE[serviceProvider];
  if (!productCode) {
    console.warn(
      `Unknown serviceProvider: ${serviceProvider} for reseller ${resellerId}`,
    );
    return null;
  }
  const channel = channelCode || "0000";
  return `${resellerId}#${productCode}#${channel}`;
}

export interface InvoiceRecord {
  resellerId: string;
  billingPeriod: string;
  productName: string;
  amount: number;
  invoiceCurrencyCode: string;
  batchSourceName: string;
  noteText?: string;
  glPostedDate?: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
  functionalAmount: number;
  functionalCurrencyCode: string;
  exchangeRate: number;
}

let cachedClient: DynamoDBDocumentClient | null = null;
let cacheExpiry = 0;

/**
 * Assumes the cross-account PortalRead role and returns a DDB DocumentClient.
 * Caches credentials for 50 minutes (STS session lasts 1 hour).
 */
async function getInvoiceTrackerDocClient(): Promise<DynamoDBDocumentClient> {
  if (cachedClient && Date.now() < cacheExpiry) return cachedClient;

  const config = getConfig();
  const cognitoCreds = Auth.essentialCredentials(
    await Auth.currentCredentials(),
  );

  const sts = new STSClient({
    credentials: cognitoCreds,
    region: config.region,
  });
  const assumed = await sts.send(
    new AssumeRoleCommand({
      RoleArn: config.roleArn,
      RoleSessionName: "BartPortalInvoiceRead",
    }),
  );

  const creds = assumed.Credentials;
  if (!creds?.AccessKeyId || !creds?.SecretAccessKey || !creds?.SessionToken) {
    throw new Error("STS AssumeRole returned incomplete credentials");
  }

  const crossAccountCreds = {
    accessKeyId: creds.AccessKeyId,
    secretAccessKey: creds.SecretAccessKey,
    sessionToken: creds.SessionToken,
  };

  const ddb = new DynamoDBClient({
    credentials: crossAccountCreds,
    region: config.region,
  });
  cachedClient = DynamoDBDocumentClient.from(ddb);
  cacheExpiry = Date.now() + 50 * 60 * 1000;
  return cachedClient;
}

/**
 * Queries all invoices for a given resellerId, handling DDB pagination.
 * Filters by billingPeriod range: from (inclusive lower) and to (inclusive upper month).
 */
export async function queryInvoices(
  resellerId: string,
  billingPeriodFrom?: string,
  billingPeriodTo?: string,
): Promise<InvoiceRecord[]> {
  const config = getConfig();
  const client = await getInvoiceTrackerDocClient();

  let keyCondition = "resellerId = :pk";
  const exprValues: Record<string, string> = { ":pk": resellerId };
  const exprNames: Record<string, string> = {};

  if (billingPeriodFrom && billingPeriodTo) {
    keyCondition += " AND #sk BETWEEN :skFrom AND :skTo";
    exprValues[":skFrom"] = billingPeriodFrom;
    exprValues[":skTo"] = billingPeriodTo + "~";
    exprNames["#sk"] = "billingPeriod#productName";
  } else if (billingPeriodFrom) {
    keyCondition += " AND #sk >= :skFrom";
    exprValues[":skFrom"] = billingPeriodFrom;
    exprNames["#sk"] = "billingPeriod#productName";
  } else if (billingPeriodTo) {
    keyCondition += " AND #sk <= :skTo";
    exprValues[":skTo"] = billingPeriodTo + "~";
    exprNames["#sk"] = "billingPeriod#productName";
  }

  const items: Record<string, unknown>[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;

  do {
    const resp = await client.send(
      new QueryCommand({
        TableName: config.tableName,
        KeyConditionExpression: keyCondition,
        ExpressionAttributeValues: exprValues,
        ...(Object.keys(exprNames).length > 0 && {
          ExpressionAttributeNames: exprNames,
        }),
        ...(exclusiveStartKey && { ExclusiveStartKey: exclusiveStartKey }),
      }),
    );
    items.push(...(resp.Items ?? []));
    exclusiveStartKey = resp.LastEvaluatedKey;
  } while (exclusiveStartKey);

  return items.map((item) => {
    const sk = item["billingPeriod#productName"] as string;
    const hashIdx = sk.indexOf("#");
    return {
      resellerId: item.resellerId as string,
      billingPeriod: sk.substring(0, hashIdx),
      productName: sk.substring(hashIdx + 1),
      amount: item.invoiceAmount as number,
      invoiceCurrencyCode: item.invoiceCurrencyCode as string,
      batchSourceName: item.batchSourceName as string,
      noteText: item.noteText as string | undefined,
      glPostedDate: item.glPostedDate as string | undefined,
      lastUpdatedDate: item.lastUpdatedDate as string | undefined,
      lastUpdatedBy: item.lastUpdatedBy as string | undefined,
      functionalAmount: item.functionalAmount as number,
      functionalCurrencyCode: item.functionalCurrencyCode as string,
      exchangeRate: item.exchangeRate as number,
    };
  });
}

/**
 * Queries invoices for multiple resellerIds with concurrency limit.
 * Accepts an optional boundary map (resellerId -> first IB period) to filter
 * tracker records to only those from the current config onwards.
 * Accepts reportMonth (YYYY-MM) as upper boundary for billing periods.
 * Returns a map of resellerId -> InvoiceRecord[] (or null if query failed).
 */
export async function queryInvoicesForResellers(
  resellerIds: string[],
  boundaries?: Record<string, string>,
  reportMonth?: string,
): Promise<Record<string, InvoiceRecord[] | null>> {
  const BATCH_SIZE = 10;
  const results: [string, InvoiceRecord[] | null][] = [];

  for (let i = 0; i < resellerIds.length; i += BATCH_SIZE) {
    const batch = resellerIds.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.all(
      batch.map(async (id) => {
        try {
          const from = boundaries?.[id];
          const records = await queryInvoices(id, from, reportMonth);
          return [id, records] as [string, InvoiceRecord[] | null];
        } catch (err) {
          console.error(`InvoiceTracker query failed for ${id}:`, err);
          return [id, null] as [string, InvoiceRecord[] | null];
        }
      }),
    );
    results.push(...batchResults);
  }

  return Object.fromEntries(results);
}
